'use client';

import React from 'react';
import { useStrategyForm } from 'src/hooks/strategy/useStrategyForm';
import { StrategyTimeFrame } from '../StrategyTimeFrame';
import { PulseSlider } from 'src/components/PulseUI';

const BasicInformation = ({ strategyMode, strategyType }) => {
  const {
    formData,
    notReferralsData,
    patientJourneyData,
    updateLabel,
    updateDescription,
    updateRefreshEvent,
    updateMaxOpportunities,
    updateNotReferralsData,
    updatePatientJourneyData,
  } = useStrategyForm(strategyMode);

  // Max Opportunities input handling
  const handleMaxOpportunitiesChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (value === '') {
      updateMaxOpportunities('' as any);
    } else if (/^\d+$/.test(value)) {
      const numValue = parseInt(value);
      if (numValue >= 1 && numValue <= 1000) {
        updateMaxOpportunities(numValue);
      }
    }
  };

  const handleMaxOpportunitiesBlur = (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    let value = parseInt(e.target.value) || 1;
    if (value < 1) value = 1;
    if (value > 1000) value = 1000;
    updateMaxOpportunities(value);
  };

  const handleRadiusChange = (newValue: number) => {
    // Dispatch to the correct Redux action based on strategy type
    if (strategyType === 'PATIENT_JOURNEY') {
      updatePatientJourneyData({ radius: newValue });
    } else if (strategyType === 'NOT_REFERRING') {
      updateNotReferralsData({ radius: newValue });
    }
  };

  const renderRightColumn = () => {
    switch (strategyType) {
      case 'REFERRALS':
      case 'TREATING_VOLUME':
        return (
          <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-200 w-fit h-fit">
            <div className="flex items-start gap-3">
              <label className="text-primary text-sm font-semibold leading-tight pt-2">
                Max Opportunities
              </label>
              <div className="flex flex-col gap-1">
                <input
                  type="text"
                  value={formData.max_opportunities}
                  onChange={handleMaxOpportunitiesChange}
                  onBlur={handleMaxOpportunitiesBlur}
                  className="w-20 px-3 py-2 border border-neutral-200 rounded-full focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 bg-white text-center font-medium"
                />
                <div className="text-xs text-neutral-400">Range: 1-1000</div>
              </div>
            </div>
          </div>
        );
      case 'NOT_REFERRING':
      case 'PATIENT_JOURNEY':
        return (
          <div className="flex flex-col gap-3">
            <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-200 w-fit">
              <div className="flex items-start gap-3">
                <label className="text-primary text-sm font-semibold leading-tight pt-2">
                  Max Opportunities
                </label>
                <div className="flex flex-col gap-1">
                  <input
                    type="text"
                    value={formData.max_opportunities}
                    onChange={handleMaxOpportunitiesChange}
                    onBlur={handleMaxOpportunitiesBlur}
                    className="w-20 px-3 py-2 border border-neutral-200 rounded-full focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 bg-white text-center font-medium"
                  />
                  <div className="text-xs text-neutral-400">Range: 1-1000</div>
                </div>
              </div>
            </div>
            <div className="flex-1 flex flex-col min-w-0">
              <PulseSlider
                value={
                  strategyType === 'PATIENT_JOURNEY'
                    ? patientJourneyData?.radius ?? 0
                    : notReferralsData?.radius ?? 0
                }
                onChange={handleRadiusChange}
              />
            </div>
          </div>
        );
      case 'SCOUT_LIST':
      case 'FRESH_FACES':
        return (
          <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-200">
            <div className="flex flex-col gap-4">
              {/* Timeframe Group */}
              <StrategyTimeFrame
                selectedTimeframe={formData.refresh_event}
                onSelect={(timeframe) =>
                  updateRefreshEvent(timeframe as 'NEVER' | 'MONTHLY')
                }
              />

              {/* Max Opportunities Group */}
              <div className="flex flex-col gap-1">
                <label className="text-primary text-sm font-semibold leading-tight">
                  Max Opportunities
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={formData.max_opportunities}
                    onChange={handleMaxOpportunitiesChange}
                    onBlur={handleMaxOpportunitiesBlur}
                    className="w-20 px-3 py-2 border border-neutral-200 rounded-full focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 bg-white text-center font-medium"
                  />
                  <span className="text-xs text-neutral-500">per period</span>
                </div>
                <div className="text-xs text-neutral-400">Range: 1-1000</div>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="bg-neutral-50 rounded-lg p-8 border border-neutral-50 w-full h-full flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-center max-w-xs">
              <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-neutral-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div className="flex flex-col gap-1">
                <h3 className="text-neutral-700 text-sm font-semibold">
                  No Strategy Type Selected
                </h3>
                <p className="text-neutral-500 text-xs leading-relaxed">
                  Select a strategy type below to configure its specific options
                </p>
              </div>
            </div>
          </div>
        );
    }
  };
  return (
    <>
      <div className="flex flex-col">
        <div className="text-lg font-medium text-primary leading-tight">
          Basic Information
        </div>
        <p className="text-tertiary text-xs leading-tight">
          Configure your strategy&apos;s core settings
        </p>
      </div>

      {/* Two-column layout on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Left Column - Core Strategy Info */}
        <div className="flex gap-4 flex-col h-full">
          <div className="flex flex-col gap-1 flex-1">
            <label className="self-stretch justify-start text-primary text-sm font-semibold font-['Inter'] leading-tight">
              Strategy Name
            </label>
            <input
              type="text"
              value={formData.label}
              onChange={(e) => updateLabel(e.target.value)}
              className="w-full px-4 py-3 border border-neutral-200 rounded-full focus:outline-none bg-white"
              placeholder="Enter strategy name"
            />
          </div>

          <div className="flex flex-col gap-1 flex-1">
            <label className="self-stretch justify-start text-primary text-sm font-semibold font-['Inter'] leading-tight">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => updateDescription(e.target.value)}
              className="w-full px-4 py-3 border border-neutral-200 rounded-xl bg-white shadow-sm resize-none focus:outline-none flex-1"
              placeholder="Describe your strategy"
            />
          </div>
        </div>

        {/* Right Column - Configuration Options */}
        {renderRightColumn()}
      </div>
    </>
  );
};

export default BasicInformation;
